import {Injectable} from '@angular/core'
import {HttpClient} from '@angular/common/http'

import {map} from 'rxjs/operators'
import {environment} from 'environments/environment'

@Injectable()
export class AppConfigService {
  apiUrl: string
  gtmId: string = null
  development: boolean = false
  staging: boolean = false
  production: boolean = false
  /**
   * Keycloak/OIDC login configuration. Two mutually-exclusive shapes:
   *
   *  - BFF: set {@link ssoLoginUrl} to a backend entry point that owns the
   *    OIDC client and redirects back with the token. No client_id needed here.
   *
   *  - Direct Authorization Code + PKCE: set {@link ssoAuthorizationEndpoint} +
   *    {@link ssoClientId} (+ optionally {@link ssoTokenEndpoint}); the SPA
   *    redirects to Keycloak with `response_type=code`, then exchanges the
   *    returned code for a JWT at the token endpoint.
   *
   * All empty ⇒ the SSO login option is hidden and everything stays inert.
   */
  ssoLoginUrl: string = null
  /** Keycloak public authorization endpoint (from the realm discovery doc). */
  ssoAuthorizationEndpoint: string = null
  /**
   * Keycloak token endpoint, used to exchange the authorization `code` for a
   * JWT (Authorization Code + PKCE). Optional: when unset the service derives it
   * from {@link ssoAuthorizationEndpoint} by swapping the trailing `/auth` for
   * `/token`. Must be reachable from the browser with CORS allowing this origin.
   */
  ssoTokenEndpoint: string = null
  /** Public client id registered in the realm with standard flow + PKCE. */
  ssoClientId: string = null
  /** OIDC scope(s); defaults to `openid` in the service when unset. */
  ssoScope: string = null
  /** Legacy/BFF response_type hint; the direct flow always uses `code` + PKCE. */
  ssoResponseType: string = null
  /** Display-ready date for the production Keycloak migration notice. */
  ssoMigrationDate: string = null

  constructor(private http: HttpClient) {}

  load(): Promise<any> {
    return this.http
      .get<{
        apiUrl: string
        gtmId: string | null
        development: boolean
        staging: boolean
        production: boolean
        ssoLoginUrl?: string | null
        ssoAuthorizationEndpoint?: string | null
        ssoTokenEndpoint?: string | null
        ssoClientId?: string | null
        ssoScope?: string | null
        ssoResponseType?: string | null
        ssoMigrationDate?: string | null
      }>('assets/config.json')
      .pipe(
        map((res) => {
          this.apiUrl = res.apiUrl
          this.gtmId = res.gtmId
          this.development = res.development
          this.staging = res.staging
          this.production = res.production
          this.ssoLoginUrl = res.ssoLoginUrl ?? null
          this.ssoAuthorizationEndpoint = res.ssoAuthorizationEndpoint ?? null
          this.ssoTokenEndpoint = res.ssoTokenEndpoint ?? null
          this.ssoClientId = res.ssoClientId ?? null
          this.ssoScope = res.ssoScope ?? null
          this.ssoResponseType = res.ssoResponseType ?? null
          this.ssoMigrationDate = res.ssoMigrationDate ?? null
          const envUpdate: {[key: string]: any} = {}

          if (this.apiUrl !== undefined) envUpdate.apiUrl = this.apiUrl
          if (this.production !== undefined)
            envUpdate.production = this.production
          if (this.staging !== undefined) envUpdate.staging = this.staging
          if (this.development !== undefined)
            envUpdate.development = this.development
          this.initializeGTM(this.gtmId)
          Object.assign(environment, envUpdate)
        }),
      )
      .toPromise()
  }

  private initializeGTM(gtmId: string): void {
    if (!gtmId) return

    // Add GTM script
    const gtmScript = document.createElement('script')
    gtmScript.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `
    document.head.appendChild(gtmScript)

    // Add GTM noscript
    const gtmNoscript = document.createElement('noscript')
    const iframe = document.createElement('iframe')
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`
    iframe.height = '0'
    iframe.width = '0'
    iframe.style.display = 'none'
    iframe.style.visibility = 'hidden'
    gtmNoscript.appendChild(iframe)
    document.body.insertBefore(gtmNoscript, document.body.firstChild)
  }
}
